import React from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  MapPin, 
  Navigation, 
  Clock, 
  Route as RouteIcon,
  X,
  Maximize2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { GpsTrip } from '@/hooks/use-gps-trips';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface TripMapViewProps {
  trip: GpsTrip;
  routePoints?: Array<{ lat: number; lon: number; ts: string }>;
  onClose?: () => void;
  onFullscreen?: () => void;
}

export const TripMapView: React.FC<TripMapViewProps> = ({
  trip,
  routePoints = [],
  onClose,
  onFullscreen
}) => {
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDistance = (km: number) => {
    return `${km.toFixed(1)} km`;
  };

  // Create the route line from route points or just start/end
  const routeLine = routePoints.length > 0 
    ? routePoints.map(point => [point.lat, point.lon] as [number, number])
    : [[trip.start_location.lat, trip.start_location.lon], [trip.end_location.lat, trip.end_location.lon]] as [number, number][];

  // Calculate map bounds to fit the route
  const bounds = routeLine.length > 0 ? L.latLngBounds(routeLine) : undefined;

  // Create custom icons for start and end points
  const startIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  const endIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RouteIcon className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Detalhes da Viagem</CardTitle>
          </div>
          <div className="flex gap-2">
            {onFullscreen && (
              <Button variant="outline" size="sm" onClick={onFullscreen}>
                <Maximize2 className="h-4 w-4" />
              </Button>
            )}
            {onClose && (
              <Button variant="outline" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Trip Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Navigation className="h-4 w-4 text-blue-600" />
              Veículo
            </div>
            <div className="text-lg font-semibold">
              {trip.vehicle_plate || trip.device_name || 'N/A'}
            </div>
            {trip.vehicle_make && trip.vehicle_model && (
              <div className="text-sm text-muted-foreground">
                {trip.vehicle_make} {trip.vehicle_model}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4 text-green-600" />
              Duração & Distância
            </div>
            <div className="flex gap-2">
              <Badge variant="outline">{formatDuration(trip.duration_minutes)}</Badge>
              <Badge variant="outline">{formatDistance(trip.distance_km)}</Badge>
            </div>
            {trip.idle_time_minutes > 0 && (
              <div className="text-sm text-muted-foreground">
                Tempo parado: {formatDuration(trip.idle_time_minutes)}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="h-4 w-4 text-purple-600" />
              Período
            </div>
            <div className="text-sm">
              <div>Início: {format(new Date(trip.start_time), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</div>
              <div>Fim: {format(new Date(trip.end_time), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0 flex-1">
        <div className="h-96 w-full">
          <MapContainer
            bounds={bounds}
            style={{ height: '100%', width: '100%' }}
            className="rounded-b-lg"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Route Line */}
            {routeLine.length > 1 && (
              <Polyline
                positions={routeLine}
                color="#3b82f6"
                weight={4}
                opacity={0.8}
              />
            )}

            {/* Start Marker */}
            <Marker
              position={[trip.start_location.lat, trip.start_location.lon]}
              icon={startIcon}
            >
              <Popup>
                <div className="space-y-2">
                  <div className="font-semibold flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-green-600" />
                    Início da Viagem
                  </div>
                  <div className="text-sm">
                    {format(new Date(trip.start_time), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                  </div>
                  {trip.start_location?.address?.formatted && (
                    <div className="text-sm text-muted-foreground">
                      {trip.start_location.address.formatted}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {trip.start_location.lat.toFixed(6)}, {trip.start_location.lon.toFixed(6)}
                  </div>
                </div>
              </Popup>
            </Marker>

            {/* End Marker */}
            <Marker
              position={[trip.end_location.lat, trip.end_location.lon]}
              icon={endIcon}
            >
              <Popup>
                <div className="space-y-2">
                  <div className="font-semibold flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-red-600" />
                    Fim da Viagem
                  </div>
                  <div className="text-sm">
                    {format(new Date(trip.end_time), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                  </div>
                  {trip.end_location?.address?.formatted && (
                    <div className="text-sm text-muted-foreground">
                      {trip.end_location.address.formatted}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {trip.end_location.lat.toFixed(6)}, {trip.end_location.lon.toFixed(6)}
                  </div>
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>
      </CardContent>
    </Card>
  );
};